// The cart lines a monogrammed product adds up to. Shared by the product form
// and the cart's monogram editor so a monogram edited in the cart is written
// exactly the same way as one added from a product form.

import {
  monogramSummary,
  encodeMonogram,
  monogramLineItemProperties,
} from "./monogramTypeConfig";
import { monogramLeadTime } from "../../helpers/stockMessage/stockMessage";

/**
 * The quantity the monogram line is added at: one monogram per item in the
 * product, so a two-pillowcase set at quantity 2 is monogrammed four times.
 *
 * @param {object} item { variant, quantity } — the product variant (carrying
 *        its product's `itemCount`) and how many are being added
 * @returns {number}
 */
export function monogramQuantity({ variant, quantity }) {
  return quantity * Math.max(1, parseInt(variant?.product?.itemCount, 10) || 1);
}

/**
 * Build the pair of cart lines for a monogrammed product: the product line
 * followed by its monogram line, tied together by a freshly generated id
 * (`_uid` on the product, `_monogram_for` on the monogram) — the link the cart
 * bundles the two lines by.
 *
 * `properties` are the product line's existing properties (anything unrelated
 * to monogramming, preserved when re-adding an edited line); the bundle
 * plumbing is always regenerated here.
 *
 * @param {object} item { variant, quantity, monogram, properties }
 * @returns {object[]} [productLine, monogramLine]
 */
export default function monogramLineItems({
  variant,
  quantity,
  monogram,
  properties,
}) {
  const uid = crypto.randomUUID();
  const summary = monogramSummary(monogram.data);

  return [
    {
      id: window.gidToId(variant.id),
      quantity,
      properties: {
        ...properties,
        _uid: uid,
        _monogram_summary: summary,
        _final_sale: "true",
        _non_returnable: "true",
      },
    },
    {
      id: window.gidToId(monogram.variant.id),
      quantity: monogramQuantity({ variant, quantity }),
      properties: {
        ...monogramLineItemProperties(monogram.data, uid, monogram.selection),
        _monogram_summary: summary,
        // The placement is fixed once the line is in the cart, so its lead time
        // is saved here rather than recomputed — the cart has no placement to
        // read it back off.
        _monogram_lead_time: monogramLeadTime(monogram),
        _monogram: encodeMonogram(monogram.data, {
          productTitle: variant.product?.title,
          sku: variant.sku,
        }),
        _final_sale: "true",
        _non_returnable: "true",
      },
    },
  ];
}
